import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Person,
  Notifications,
  Security,
  Palette,
  Language,
  Logout,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService, User } from '../../services/authService';
import { androidColors } from '../../theme/androidTheme';
import Layout from '../../components/Layout/Layout';
import PageTitle from '../../components/PageTitle/PageTitle';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const userInfo = await authService.getUserInfo();
        if (userInfo) {
          const newUser: User = {
            id: userInfo.id || '',
            firstName: userInfo.firstname || '',
            lastName: userInfo.lastname || '',
            email: userInfo.email || '',
            premium: userInfo.premium || 'no',
            onboard: userInfo.onboard || 'no',
            createdAt: userInfo.createdAt || new Date().toISOString(),
          };
          setUser(newUser);
        } else {
          const currentUser = authService.getUser();
          if (currentUser) {
            setUser(currentUser);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données utilisateur:', error);
        const currentUser = authService.getUser();
        if (currentUser) {
          setUser(currentUser);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);
  
  const [settings, setSettings] = useState({
    notifications: true,
    darkMode: true,
    autoRefresh: true,
    priceAlerts: false,
  });

  const handleSettingChange = (setting: keyof typeof settings) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const SettingCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    description: string;
    children: React.ReactNode;
  }> = ({ icon, title, description, children }) => (
    <Card
      sx={{
        backgroundColor: androidColors.cardBackground,
        border: `1px solid ${androidColors.borderColor}`,
        borderRadius: '12px',
        mb: 2,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box sx={{ mr: 2, mt: 0.5 }}>
              {icon}
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: androidColors.textColor,
                  fontWeight: 'bold',
                  mb: 0.5,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: androidColors.textSecondary }}
              >
                {description}
              </Typography>
            </Box>
          </Box>
          <Box>
            {children}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : t('common.user');
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase();
  const userPlan = user?.premium === 'yes' ? t('common.premium') : t('common.lifetime');

  return (
    <Layout title={t('settings.title')}>
      <PageTitle title={t('settings.title')} />
      {/* Informations utilisateur */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
            mb: 3,
          }}
        >
          {t('settings.user_profile')}
        </Typography>
        
        <Card
          sx={{
            backgroundColor: androidColors.cardBackground,
            border: `1px solid ${androidColors.borderColor}`,
            borderRadius: '12px',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isLoading ? (
                <>
                  <Skeleton
                    variant="circular"
                    width={60}
                    height={60}
                    sx={{ bgcolor: `${androidColors.primaryGold}20`, mr: 3 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width="50%"
                      height={28}
                      sx={{ bgcolor: `${androidColors.primaryGold}20`, mb: 1 }}
                    />
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={24}
                      sx={{ bgcolor: `${androidColors.primaryGold}20` }}
                    />
                  </Box>
                </>
              ) : (
                <>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: androidColors.primaryGold,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 3,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: androidColors.darkBackground,
                    fontWeight: 'bold',
                  }}
                >
                  {userInitials}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: androidColors.textColor,
                    fontWeight: 'bold',
                    mb: 0.5,
                  }}
                >
                  {userName}
                </Typography>
                <Typography
                  variant="body2"
                      sx={{
                        color: androidColors.primaryGold,
                        fontWeight: 'bold',
                      }}
                >
                  {userPlan}
                </Typography>
              </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Paramètres de l'application */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
            mb: 3,
          }}
        >
          {t('common.preferences')}
        </Typography>

        <SettingCard
          icon={<Notifications sx={{ color: androidColors.primaryGold }} />}
          title={t('settings.notifications')}
          description={t('settings.notifications_desc')}
        >
          <FormControlLabel
            control={
              <Switch
                checked={settings.notifications}
                onChange={() => handleSettingChange('notifications')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: androidColors.primaryGold,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: androidColors.primaryGold,
                  },
                }}
              />
            }
            label=""
          />
        </SettingCard>

        <SettingCard
          icon={<Palette sx={{ color: androidColors.primaryGold }} />}
          title={t('settings.dark_mode')}
          description={t('settings.dark_mode_desc')}
        >
          <FormControlLabel
            control={
              <Switch
                checked={settings.darkMode}
                onChange={() => handleSettingChange('darkMode')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: androidColors.primaryGold,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: androidColors.primaryGold,
                  },
                }}
              />
            }
            label=""
          />
        </SettingCard>

        <SettingCard
          icon={<Security sx={{ color: androidColors.primaryGold }} />}
          title={t('settings.auto_refresh')}
          description={t('settings.auto_refresh_desc')}
        >
          <FormControlLabel
            control={
              <Switch
                checked={settings.autoRefresh}
                onChange={() => handleSettingChange('autoRefresh')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: androidColors.primaryGold,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: androidColors.primaryGold,
                  },
                }}
              />
            }
            label=""
          />
        </SettingCard>

        <SettingCard
          icon={<Language sx={{ color: androidColors.primaryGold }} />}
          title={t('settings.price_alerts')}
          description={t('settings.price_alerts_desc')}
        >
          <FormControlLabel
            control={
              <Switch
                checked={settings.priceAlerts}
                onChange={() => handleSettingChange('priceAlerts')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: androidColors.primaryGold,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: androidColors.primaryGold,
                  },
                }}
              />
            }
            label=""
          />
        </SettingCard>

        <SettingCard
          icon={<Language sx={{ color: androidColors.primaryGold }} />}
          title={t('settings.language')}
          description={t('settings.language_desc')}
        >
          <LanguageSwitcher size="medium" fullWidth label={t('settings.language')} />
        </SettingCard>
      </Box>

      {/* Actions */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
            mb: 3,
          }}
        >
          Actions
        </Typography>

        <Alert
          severity="info"
          sx={{
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            border: '1px solid rgba(33, 150, 243, 0.3)',
            color: androidColors.textColor,
            mb: 3,
            '& .MuiAlert-icon': {
              color: '#2196F3',
            },
          }}
        >
          {t('common.saved_browser')}
        </Alert>

        <Card
          sx={{
            backgroundColor: androidColors.cardBackground,
            border: `1px solid ${androidColors.borderColor}`,
            borderRadius: '12px',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Logout sx={{ color: androidColors.error, mr: 2 }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: androidColors.textColor,
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    {t('common.logout')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: androidColors.textSecondary }}
                  >
                    {t('common.logout_desc')}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                onClick={handleLogout}
                sx={{
                  borderColor: androidColors.error,
                  color: androidColors.error,
                  '&:hover': {
                    borderColor: androidColors.error,
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  },
                }}
              >
                {t('common.logout')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default Settings;